import { SocketClient } from "cbcore-ts"
import { IF, nil, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHApplicationMode, RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHTextField } from "../../Custom components/RH/RHTextField"
import type { RHValidationIssue } from "../../Custom components/RH/RHValidationIssuesDialogView"
import {
    RHButtons,
    RHColors,
    RHFont,
    RHForms,
    RHSpacing
} from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHCompanyMembershipRequestRole } from "../../SocketClientFunctions"
import { RHCompanyMemberPresentation } from "./CompanyMemberPresentation"


export class CompanyAdministrationInviteMemberView extends UIView {

    readonly titleLabel = new UITextView(nil, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Kutsu liige",
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            text: "Kasutaja saab kutse e-posti ja teavituse kaudu.",
            textColor: RHColors.textSecondary,
            isSingleLine: NO
        })
        .addedAsSubviewToView(this)

    readonly nameField = new RHTextField()
        .configuredWithObject({ placeholderText: "Kasutaja nimi (valikuline)" })
        .addedAsSubviewToView(this)

    readonly emailField = new RHTextField()
        .configuredWithObject({ placeholderText: "Kasutaja e-posti aadress" })
        .addedAsSubviewToView(this)

    readonly rolesLabel = new UITextView()
        .configuredWithObject({
            text: "Rollid ettevõttes",
            textColor: RHColors.textHeading,
            isSingleLine: NO,
            style: { fontWeight: RHFont.weightSemibold }
        })
        .addedAsSubviewToView(this)

    readonly administratorCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.administrator
            }
        })
        .addedAsSubviewToView(this)

    readonly administratorExplanationLabel = new UITextView()
        .configuredWithObject({
            text: RHCompanyMemberPresentation.administratorRoleExplanationForProcurerEligibility(YES, YES),
            textColor: RHColors.textSecondary,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall, fontWeight: RHFont.weightRegular }
        })
        .addedAsSubviewToView(this)

    readonly procurerCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.procurer
            },
            selected: YES
        })
        .addedAsSubviewToView(this)

    readonly procurerIneligibleLabel = new UITextView()
        .configuredWithObject({
            text: RHCompanyAdministrationViewTokens.procurerIneligibleExplanation,
            textColor: RHColors.warningAmberText,
            isSingleLine: NO,
            hidden: YES,
            style: { fontSize: RHFont.sizeSmall }
        })
        .addedAsSubviewToView(this)

    readonly offererCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.offerer
            }
        })
        .addedAsSubviewToView(this)

    readonly warningAlertView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            hidden: YES
        })
        .addedAsSubviewToView(this)

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista", style: { fontSize: RHButtons.compactFontSize } },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly sendButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Saada kutse", style: { fontSize: RHButtons.compactFontSize } },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        this.nameField,
        this.emailField,
        this.administratorCheckbox,
        this.procurerCheckbox,
        this.offererCheckbox,
        this.cancelButton,
        this.sendButton
    ])

    companyId = ""
    invitationDidComplete?: () => Promise<void>
    _isEligibleToActAsProcurer = YES


    constructor(elementID?: string) {
        super(elementID)
        this.backgroundColor = UIColor.whiteColor
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dialogView?.dismiss()
        }
        this.administratorCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._applyRoleSelectionRules()
        }
        this.procurerCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._applyRoleSelectionRules()
        }
        this.offererCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._applyRoleSelectionRules()
        }
        this.sendButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await this._sendInvitation()
        }
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager!)
    }


    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHPageFocusManager.shared.popLayer()
    }


    set isEligibleToActAsProcurer(isEligible: boolean) {
        this._isEligibleToActAsProcurer = isEligible
        this.procurerIneligibleLabel.hidden = isEligible
        this.procurerCheckbox.hoverText = IF(isEligible)(() => undefined)
            .ELSE(() => RHCompanyAdministrationViewTokens.procurerIneligibleExplanation)
        this.administratorExplanationLabel.text =
            RHCompanyMemberPresentation.administratorRoleExplanationForProcurerEligibility(isEligible, YES)
        this._applyRoleSelectionRules()
        this.setNeedsLayout()
    }


    selectDefaultRoleForApplicationMode(applicationMode: RHApplicationMode) {
        const shouldSelectProcurer = applicationMode === "procurer" && this._isEligibleToActAsProcurer
        this.administratorCheckbox.selected = NO
        this.procurerCheckbox.selected = shouldSelectProcurer
        this.offererCheckbox.selected = !shouldSelectProcurer
        this._applyRoleSelectionRules()
    }


    _applyRoleSelectionRules() {
        const administratorSelected = this.administratorCheckbox.selected
        if (administratorSelected && this._isEligibleToActAsProcurer) {
            this.procurerCheckbox.selected = YES
        }
        if (administratorSelected) {
            this.offererCheckbox.selected = YES
        }
        this.administratorCheckbox.enabled = YES
        this.procurerCheckbox.enabled = !administratorSelected && this._isEligibleToActAsProcurer
        this.offererCheckbox.enabled = !administratorSelected
        if (!this._isEligibleToActAsProcurer) {
            this.procurerCheckbox.selected = NO
        }
    }


    get selectedRoles(): RHCompanyMembershipRequestRole[] {
        if (this.administratorCheckbox.selected) {
            return ["administrator"]
        }
        const roles: RHCompanyMembershipRequestRole[] = []
        if (this.procurerCheckbox.selected) {
            roles.push("procurer")
        }
        if (this.offererCheckbox.selected) {
            roles.push("offerer")
        }
        return roles
    }


    showError(message: string) {
        this.warningAlertView.text = message
        this.warningAlertView.hidden = NO
        this.setNeedsLayoutUpToRootView()
    }


    showValidationIssues(validationIssues: RHValidationIssue[]) {
        const issueList = validationIssues
            .map((validationIssue, issueIndex) => `${issueIndex + 1}. ${validationIssue.message}`)
            .join(" ")
        this.showError(`Palun paranda järgmised väljad: ${issueList}`)
        validationIssues[0]?.focusTarget.focus()
    }


    async _sendInvitation() {
        const email = this.emailField.text.trim()
        const name = this.nameField.text.trim()
        const requestedRoles = this.selectedRoles
        const validationIssues: RHValidationIssue[] = []
        if (!email) {
            validationIssues.push({
                focusTarget: this.emailField,
                message: "Kasutaja e-posti aadress on puudu."
            })
        }
        if (requestedRoles.length === 0) {
            validationIssues.push({
                focusTarget: this.administratorCheckbox,
                message: "Vali vähemalt üks kasutaja roll."
            })
        }
        if (validationIssues.length > 0) {
            this.showValidationIssues(validationIssues)
            return
        }

        this.loading = YES
        try {
            const response = await SocketClient.InviteCompanyMember({
                companyId: this.companyId,
                email,
                name: name || undefined,
                requestedRoles,
                sendEmail: YES
            })
            if (response.result?.success) {
                await this.invitationDidComplete?.()
                this.dialogView?.dismiss()
                if (response.result.wasNotificationDeferred) {
                    CBDialogViewShower.alert(
                        "Kutse salvestati. Teavituse kohaletoimetamine jätkub taustal."
                    )
                }
                else {
                    CBDialogViewShower.alert("Kutse on saadetud.")
                }
            }
            else {
                this.showError(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Kutse saatmine ebaõnnestus."
                    ) ?? "Kutse saatmine ebaõnnestus."
                )
            }
        }
        catch (error) {
            console.error("Company invitation failed:", error)
            this.showError("Kutse saatmine ebaõnnestus.")
        }
        finally {
            this.loading = NO
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(this.core.paddingLength)

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.small, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(RHSpacing.medium, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.nameField)
            .rectangleForNextRow(RHSpacing.small, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.emailField)
            .rectangleForNextRow(RHSpacing.medium, this.rolesLabel)
            .assignedAsFrameOfView(this.rolesLabel)
            .rectangleForNextRow(RHSpacing.small, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.administratorCheckbox)
            .rectangleForNextRow(RHSpacing.tiny, this.administratorExplanationLabel)
            .assignedAsFrameOfView(this.administratorExplanationLabel)
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.procurerCheckbox)
            .IF(!this.procurerIneligibleLabel.hidden)
            .rectangleForNextRow(RHSpacing.tiny, this.procurerIneligibleLabel)
            .assignedAsFrameOfView(this.procurerIneligibleLabel)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.offererCheckbox)
            .IF(!this.warningAlertView.hidden)
            .rectangleForNextRow(RHSpacing.medium, this.warningAlertView)
            .assignedAsFrameOfView(this.warningAlertView)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.medium, RHButtons.compactHeight)
            .distributeViewsEquallyAlongWidth(
                [this.cancelButton, this.sendButton],
                RHSpacing.medium
            )
    }


    override intrinsicContentWidth() {
        return 520
    }


}
